<template>
  <StepLayout
    :back="back"
    :next="next"
    :isEditRoute="true"
    helpscoutArticle="66619e34d5f6cf2baa1ecbb2"
    buttonDataEc="envelopes_expiry_page"
  >
    <template v-slot:title>
      {{ $t('ui.envelopes.create.steps.expiry.title') }}
    </template>

    <p class="text-black">{{ $t('ui.envelopes.create.steps.expiry.noExpirySetInfo') }}</p>

    <div
      v-if="!isPendingEnvelope"
      class="flex items-center"
    >
      <ElDatePicker
        v-model="expiry"
        type="date"
        data-ec="envelopes_expiry_page"
        data-et="date_picker"
        :format="dateFormat"
        :picker-options="pickerOptions"
        :placeholder="$t('functional.pickADay')"
      />
      <BaseButton
        variant="invisible"
        @click.native="resetExpiry"
        data-ec="envelopes_expiry_page"
        data-et="reset_expiry_button"
      >
        <span>{{ $t('functional.reset') }}</span>
      </BaseButton>
    </div>
  </StepLayout>
</template>

<script setup>
// @ts-check
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router/composables';

import { useDateFormat } from '@/composables/dates/dateFormat';
import {
  useCreateUpdateEnvelope,
  useEnvelope,
  useEnvelopesStepsNavigation,
} from '@/composables/envelopes';
import {
  useUpdateWithValidation,
  useValidation,
} from '@/composables/envelopes/validation';
import { watchFalse } from '@/composables/utils/watchFalse';
import { EnvelopeUpdate } from '@/store/modules/envelopes/models/Envelope';
import i18n from '@/locales';

import StepLayout from '@/components/layouts/StepLayout';
import BaseButton from '@/components/library/buttons/BaseButton.vue';

const route = useRoute();

// get envelope data
const { envelope, isPendingEnvelope } = useEnvelope(route.params.fingerprint);
const { updateEnvelope } = useCreateUpdateEnvelope(route.params.fingerprint);

const dateFormat = useDateFormat();

// initialise local state
const expiry = ref();
watchFalse(isPendingEnvelope, () => {
  if (envelope.value?.next_expire) {
    expiry.value = new Date(envelope.value.next_expire * 1000);
  }
});

const ExpiryUpdate = EnvelopeUpdate.pick({
  next_expire: true,
}).required();

const expiryUpdate = computed(() => ({
  next_expire: expiry.value ? Math.floor(expiry.value.getTime() / 1000) : null,
}));

const { isValid, data, validate } = useValidation(expiryUpdate, ExpiryUpdate);

const update = useUpdateWithValidation(updateEnvelope, data, validate);

const { back, next } = useEnvelopesStepsNavigation(update, isValid);

const pickerOptions = {
  disabledDate(time) {
    return time.getTime() < Date.now();
  },
  shortcuts: [
    {
      text: i18n.t('envelope.expiryIntervals.1day'),
      onClick(picker) {
        const day = new Date();
        day.setTime(day.getTime() + (3600 * 1000 * 24));
        picker.$emit('pick', day);
      },
    },
    {
      text: i18n.t('envelope.expiryIntervals.1week'),
      onClick(picker) {
        const week = new Date();
        week.setTime(week.getTime() + (3600 * 1000 * 24 * 7));
        picker.$emit('pick', week);
      },
    },
    {
      text: i18n.t('envelope.expiryIntervals.30days'),
      onClick(picker) {
        const thirty = new Date();
        thirty.setTime(thirty.getTime() + (3600 * 1000 * 24 * 30));
        picker.$emit('pick', thirty);
      },
    },
  ],
};

function resetExpiry() {
  expiry.value = null;
}
</script>
